import { useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Briefcase,
  Building2,
  CalendarClock,
  CheckCircle2,
  Pencil,
  Plus,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { staffService, type Department, type LeaveType, type Position } from "../services/staffService";
import { ConfirmDialog, type ConfirmRequest } from "../components/ConfirmDialog";
import AIAssistField from "../components/AIAssistField";
import { Field } from "../components/FormLayout";
import { ClientRegister, StatusDot, type RegisterColumn } from "../components/register/ClientRegister";
import { ActionPanel, DetailsModal } from "../components/register/DetailsModal";
import {
  DepartmentForm,
  LeavePolicyForm,
  PositionForm,
  type DepartmentData,
  type LeaveData,
  type PositionData,
} from "../components/staff/HRSetupForms";
import {
  HEAD_ELIGIBLE_STATUSES,
  activeStatus,
  departmentConfig,
  departmentTypeLabel,
  inPost,
  leaveConfig,
  leaveRequestsTaken,
  levelLabel,
  positionConfig,
  positionDepartment,
  recordActions,
  salaryRange,
  withDepartmentCounts,
  withHolders,
  type DepartmentRow,
  type PositionRow,
} from "../components/staff/hrSetupLogic";

type Tab = "departments" | "positions" | "leave";
const TABS: Tab[] = ["departments", "positions", "leave"];

const TAB_META: Record<Tab, { label: string; subtitle: string; icon: typeof Building2 }> = {
  departments: { label: "Departments", subtitle: "Organisational units", icon: Building2 },
  positions: { label: "Positions", subtitle: "Job titles staff are appointed to", icon: Briefcase },
  leave: { label: "Leave policies", subtitle: "Time-off allowances", icon: CalendarClock },
};

/** The step a first-time admin takes, in dependency order: positions belong
 * to departments, so departments come first. */
const SETUP_STEPS: { id: Tab; title: string; action: string; body: string }[] = [
  { id: "departments", title: "Create departments", action: "Add department", body: "The organisational units staff belong to, such as Academics or Finance." },
  { id: "positions", title: "Add positions", action: "Add position", body: "Job titles that sit inside those departments, with their levels." },
  { id: "leave", title: "Define leave policies", action: "Add leave policy", body: "Time-off allowances that staff profiles and approvals draw on." },
];

const muted = (text: string) => <span className="text-gray-300">{text}</span>;

const departmentColumns: RegisterColumn<DepartmentRow>[] = [
  { header: "Department", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
  { header: "Code", cell: (row) => row.code, exportValue: (row) => row.code, className: "font-mono text-xs text-gray-600" },
  { header: "Type", cell: (row) => departmentTypeLabel(row.department_type), exportValue: (row) => departmentTypeLabel(row.department_type), className: "text-gray-700" },
  {
    header: "Head",
    cell: (row) => row.head_name || <span className="text-amber-700">No head</span>,
    exportValue: (row) => row.head_name || "",
  },
  { header: "Staff", cell: (row) => row.staffCount, exportValue: (row) => row.staffCount, align: "right" },
  { header: "Positions", cell: (row) => row.positionCount, exportValue: (row) => row.positionCount, align: "right" },
  { header: "Status", cell: (row) => <StatusDot {...activeStatus(row)} />, exportValue: (row) => activeStatus(row).label },
];

const positionColumns: RegisterColumn<PositionRow>[] = [
  { header: "Position", cell: (row) => <span className="font-medium text-gray-900">{row.title}</span>, exportValue: (row) => row.title },
  { header: "Department", cell: positionDepartment, exportValue: positionDepartment, className: "text-gray-700" },
  { header: "Level", cell: (row) => levelLabel(row.level), exportValue: (row) => levelLabel(row.level), className: "text-gray-700" },
  {
    header: "Experience",
    cell: (row) => (row.experience_required ? `${row.experience_required} ${row.experience_required === 1 ? "yr" : "yrs"}` : muted("None")),
    exportValue: (row) => row.experience_required,
    align: "right",
  },
  { header: "Pay range", cell: (row) => salaryRange(row) || muted("Not set"), exportValue: salaryRange, className: "whitespace-nowrap tabular-nums text-gray-700" },
  {
    header: "In post",
    cell: (row) => (row.holders ? row.holders : row.is_active ? <span className="text-amber-700">Vacant</span> : muted("0")),
    exportValue: (row) => row.holders,
    align: "right",
  },
  { header: "Status", cell: (row) => <StatusDot {...activeStatus(row)} />, exportValue: (row) => activeStatus(row).label },
];

const leaveColumns: RegisterColumn<LeaveType>[] = [
  { header: "Policy", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
  { header: "Code", cell: (row) => row.code, exportValue: (row) => row.code, className: "font-mono text-xs text-gray-600" },
  { header: "Days a year", cell: (row) => row.max_days_per_year, exportValue: (row) => row.max_days_per_year, align: "right" },
  { header: "Pay", cell: (row) => (row.is_paid ? "Paid" : "Unpaid"), exportValue: (row) => (row.is_paid ? "Paid" : "Unpaid"), className: "text-gray-700" },
  {
    header: "Approval",
    cell: (row) => (row.requires_approval ? "Required" : "Not required"),
    exportValue: (row) => (row.requires_approval ? "Required" : "Not required"),
    className: "text-gray-700",
  },
  { header: "Requests taken", cell: leaveRequestsTaken, exportValue: leaveRequestsTaken, align: "right" },
  { header: "Status", cell: (row) => <StatusDot {...activeStatus(row)} />, exportValue: (row) => activeStatus(row).label },
];

function errorText(error: unknown) {
  const data = (error as { response?: { data?: Record<string, string[] | string> } })?.response?.data;
  if (!data || typeof data !== "object") return "The HR setup record could not be saved.";
  return Object.entries(data)
    .map(([field, value]) => {
      const text = Array.isArray(value) ? value.join(" ") : String(value);
      return field === "detail" || field === "non_field_errors" ? text : `${field.replaceAll("_", " ")}: ${text}`;
    })
    .join(" ");
}

type Editing =
  | { kind: "departments"; record?: Department }
  | { kind: "positions"; record?: Position }
  | { kind: "leave"; record?: LeaveType };

export default function HRSetup() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.includes(params.get("tab") as Tab) ? (params.get("tab") as Tab) : "departments";
  const [message, setMessage] = useState("");
  // Refusals get their own banner rather than sharing the green one: the
  // server explains exactly what a record is still in use by.
  const [problem, setProblem] = useState("");
  const [dialog, setDialog] = useState<ConfirmRequest | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [viewing, setViewing] = useState<{ kind: Tab; id: number } | null>(null);
  const [headChoice, setHeadChoice] = useState<string | null>(null);
  const [headReason, setHeadReason] = useState("");

  const qc = useQueryClient();
  const departments = useQuery({ queryKey: ["staff-departments"], queryFn: staffService.getDepartments });
  const positions = useQuery({ queryKey: ["staff-positions"], queryFn: staffService.getPositions });
  const profiles = useQuery({ queryKey: ["staff-profiles"], queryFn: staffService.getProfiles });
  const leaveTypes = useQuery({ queryKey: ["staff-leave-types"], queryFn: staffService.getLeaveTypes });
  const refresh = () =>
    Promise.all(
      ["staff-departments", "staff-positions", "staff-profiles", "staff-leave-types", "staff-stats"].map((key) =>
        qc.invalidateQueries({ queryKey: [key] }),
      ),
    );
  const announce = (text: string) => {
    setProblem("");
    setMessage(text);
  };
  const refuse = (error: unknown) => {
    setMessage("");
    setProblem(errorText(error));
  };

  const departmentRows = withDepartmentCounts(departments.data, profiles.data, positions.data);
  const positionRows = withHolders(positions.data, profiles.data);

  const selectTab = (next: Tab) => {
    setParams(next === "departments" ? {} : { tab: next }, { replace: true });
    setMessage("");
    setProblem("");
  };

  // --- Saving ------------------------------------------------------------------

  const saved = (noun: string, id?: number) => {
    setEditing(null);
    announce(`${noun} ${id ? "saved" : "created"}.`);
    refresh();
  };
  const saveDepartment = useMutation({
    mutationFn: ({ id, ...data }: DepartmentData & { id?: number }) =>
      id ? staffService.updateDepartment({ id, ...data }) : staffService.createDepartment({ ...data, is_active: true }),
    onSuccess: (_result, variables) => saved("Department", variables.id),
  });
  const savePosition = useMutation({
    mutationFn: ({ id, ...data }: PositionData & { id?: number }) =>
      id ? staffService.updatePosition({ id, ...data }) : staffService.createPosition({ ...data, is_active: true, is_vacant: true }),
    onSuccess: (_result, variables) => saved("Position", variables.id),
  });
  const saveLeave = useMutation({
    mutationFn: ({ id, ...data }: LeaveData & { id?: number }) =>
      id ? staffService.updateLeaveType({ id, ...data }) : staffService.createLeaveType({ ...data, is_active: true }),
    onSuccess: (_result, variables) => saved("Leave policy", variables.id),
  });
  const openForm = (next: Editing) => {
    saveDepartment.reset();
    savePosition.reset();
    saveLeave.reset();
    setEditing(next);
  };

  const headMutation = useMutation({
    mutationFn: staffService.assignDepartmentHead,
    onSuccess: () => {
      announce("Department head updated.");
      setHeadChoice(null);
      setHeadReason("");
      refresh();
    },
  });

  // Toggling active is reversible, so it needs no dialog -- but it does need
  // to say what happened. Named as hooks because they are: each calls
  // useMutation, and each is invoked unconditionally below.
  const useActivation = (setActive: (id: number, isActive: boolean) => Promise<unknown>, noun: string) =>
    useMutation({
      mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => setActive(id, isActive),
      onSuccess: (_data, variables) => {
        announce(`${noun} ${variables.isActive ? "reactivated" : "deactivated"}.`);
        refresh();
      },
      onError: refuse,
    });
  const useDelete = (remove: (id: number) => Promise<unknown>, noun: string) =>
    useMutation({
      mutationFn: remove,
      onSuccess: () => {
        setDialog(null);
        setViewing(null);
        announce(`${noun} deleted.`);
        refresh();
      },
      onError: (error: unknown) => {
        setDialog(null);
        refuse(error);
      },
    });
  const activation = {
    departments: useActivation(staffService.setDepartmentActive, "Department"),
    positions: useActivation(staffService.setPositionActive, "Position"),
    leave: useActivation(staffService.setLeaveTypeActive, "Leave policy"),
  };
  const removal = {
    departments: useDelete(staffService.deleteDepartment, "Department"),
    positions: useDelete(staffService.deletePosition, "Position"),
    leave: useDelete(staffService.deleteLeaveType, "Leave policy"),
  };
  const deleting = Object.values(removal).some((mutation) => mutation.isPending);
  const busy = deleting || Object.values(activation).some((mutation) => mutation.isPending);

  /** Ask before removing anything: none of these have a restore. */
  const confirmDelete = (kind: Tab, noun: string, name: string, id: number, warning: string) =>
    setDialog({
      title: `Delete ${noun.toLowerCase()}?`,
      body: (
        <>
          <p>
            <strong>{name}</strong> will be permanently removed. This cannot be undone.
          </p>
          <p className="mt-2 text-gray-500">{warning}</p>
        </>
      ),
      confirmLabel: `Delete ${noun.toLowerCase()}`,
      tone: "danger",
      onConfirm: () => removal[kind].mutate(id),
    });

  const open = (kind: Tab, id: number) => {
    setHeadChoice(null);
    setHeadReason("");
    headMutation.reset();
    setViewing({ kind, id });
  };

  // --- Overview ------------------------------------------------------------------

  const countsLoading = departments.isLoading || positions.isLoading || leaveTypes.isLoading;
  const counts: Record<Tab, number> = {
    departments: departments.data?.length ?? 0,
    positions: positions.data?.length ?? 0,
    leave: leaveTypes.data?.length ?? 0,
  };
  // Nothing set up yet: three zeroed cards and an empty table read as broken,
  // so a first run gets guidance and one obvious next step instead. Held back
  // until the queries settle, so it never flashes while counts load.
  const isFirstRun = !countsLoading && !departments.isError && TABS.every((item) => counts[item] === 0);
  const inactive = (rows?: { is_active: boolean }[]) => (rows ?? []).filter((row) => !row.is_active).length;
  const attention: Record<Tab, string> = {
    departments: (() => {
      const headless = (departmentRows ?? []).filter((row) => row.is_active && !row.head).length;
      return headless ? `${headless} without a head` : `${inactive(departments.data)} inactive`;
    })(),
    positions: (() => {
      const vacant = (positionRows ?? []).filter((row) => row.is_active && !row.holders).length;
      return vacant ? `${vacant} vacant` : `${inactive(positions.data)} inactive`;
    })(),
    leave: `${inactive(leaveTypes.data)} inactive`,
  };

  const addFor = (kind: Tab) => {
    selectTab(kind);
    openForm({ kind } as Editing);
  };
  const addButton = (kind: Tab) => (
    <button type="button" onClick={() => openForm({ kind } as Editing)} className="btn-primary h-8 min-h-8 px-3 text-xs">
      <Plus className="mr-1.5 h-3.5 w-3.5" /> {SETUP_STEPS.find((step) => step.id === kind)!.action}
    </button>
  );

  // --- Details ---------------------------------------------------------------------

  /** Edit / Activate-Deactivate / Delete, the same for every record. */
  const detailsFooter = (
    kind: Tab,
    record: { id: number; is_active: boolean; can_delete?: boolean; in_use_by?: Record<string, number> },
    name: string,
    noun: string,
    deleteWarning: string,
    onEdit: () => void,
  ) => {
    const { canDelete, toggleLabel } = recordActions(record);
    return (
      <>
        {canDelete && (
          <button
            type="button"
            disabled={busy}
            onClick={() => confirmDelete(kind, noun, name, record.id, deleteWarning)}
            className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        )}
        <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>
          Close
        </button>
        <button
          type="button"
          disabled={busy}
          title={record.is_active ? `Hide ${name} from new records without losing its history` : `Make ${name} selectable again`}
          onClick={() => activation[kind].mutate({ id: record.id, isActive: !record.is_active })}
          className="btn btn-outline gap-1.5"
        >
          {record.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
          {toggleLabel}
        </button>
        <button type="button" className="btn btn-primary gap-1.5" onClick={onEdit}>
          <Pencil className="h-4 w-4" /> Edit
        </button>
      </>
    );
  };

  /** Why Delete is missing, when it is. */
  const deleteNotice = (record: { is_active: boolean; can_delete?: boolean; in_use_by?: Record<string, number> }, advice: string) => {
    const { canDelete, blockedBecause } = recordActions(record);
    if (canDelete || !blockedBecause) return undefined;
    return (
      <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
        In use by {blockedBecause}, so it can't be deleted. {advice}
      </p>
    );
  };

  const history = (items: ReactNode[], empty: string) =>
    items.length ? <ol className="divide-y divide-gray-100 rounded-lg border border-gray-200">{items}</ol> : <p className="text-sm text-gray-500">{empty}</p>;

  const departmentDetails = (department: DepartmentRow) => {
    const jobs = (positionRows ?? []).filter((position) => position.department === department.id);
    const eligible = (profiles.data ?? []).filter(
      (profile) => profile.department === department.id && HEAD_ELIGIBLE_STATUSES.includes(profile.employment_status),
    );
    const current = department.head ? String(department.head) : "";
    const choice = headChoice ?? current;
    const changing = choice !== current;
    const reason = headReason.trim();
    return (
      <DetailsModal
        title={department.name}
        headline={{
          label: "Staff in post",
          value: String(department.staffCount),
          note: `${departmentTypeLabel(department.department_type)} department · ${department.positionCount} ${department.positionCount === 1 ? "position" : "positions"}`,
        }}
        status={activeStatus(department)}
        facts={[
          ["Code", <span className="font-mono">{department.code}</span>],
          ["Type", departmentTypeLabel(department.department_type)],
          ["Head", department.head_name || <span className="text-amber-700">No head</span>],
          ["Positions", String(department.positionCount)],
          ["Staff in post", String(department.staffCount)],
          ["Leadership changes", String(department.head_changes?.length ?? 0)],
        ]}
        notice={deleteNotice(department, "Deactivate it to stop it being chosen for new staff and positions.")}
        sections={[{ title: "Description", body: department.description }]}
        onClose={() => setViewing(null)}
        footer={detailsFooter(
          "departments",
          department,
          department.name,
          "Department",
          "Nothing uses this department, so it can be removed outright. If you may need its history later, deactivate it instead.",
          () => openForm({ kind: "departments", record: department }),
        )}
      >
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Positions in this department</h4>
          {jobs.length ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {jobs.map((job) => (
                <button
                  key={job.id}
                  type="button"
                  onClick={() => open("positions", job.id)}
                  className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:border-primary-300 hover:bg-primary-50"
                >
                  {job.title} <span className="tabular-nums text-gray-400">· {job.holders}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="mt-1.5 text-sm text-gray-500">No positions yet. Add them from the Positions tab.</p>
          )}
        </section>

        <ActionPanel
          title="Department head"
          description="Chosen from active staff in this department. Every change is kept with its reason."
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (changing && reason.length >= 5) {
                headMutation.mutate({ id: department.id, head: Number(choice) || null, reason });
              }
            }}
            className="space-y-3"
          >
            <Field label="Head" hint={eligible.length ? undefined : "No active staff are in this department yet, so there is nobody to choose."}>
              <select value={choice} onChange={(event) => setHeadChoice(event.target.value)} className="input mt-1">
                <option value="">Nobody (leave vacant)</option>
                {eligible.map((profile) => (
                  <option key={profile.id} value={String(profile.user)}>
                    {profile.user_name} · {profile.position_title}
                  </option>
                ))}
                {current && !eligible.some((profile) => String(profile.user) === current) && (
                  <option value={current}>{department.head_name} (not in this department)</option>
                )}
              </select>
            </Field>
            {changing && (
              <Field as="div" label="Reason for the change" hint="At least 5 characters. Kept in the leadership history.">
                <AIAssistField
                  name="reason"
                  rows={2}
                  value={headReason}
                  onChange={(next) => setHeadReason(next)}
                  placeholder="e.g. Mr Okello retired at the end of Term 2; Ms Nakato takes over."
                  context={{ key: "staff.department_head_reason", params: { department: department.id, head: choice || null } }}
                  draftLabel="Draft the reason"
                  draftHint="Reads who is handing over and taking over. Jot why first, and it writes it up."
                />
              </Field>
            )}
            {headMutation.isError && (
              <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {errorText(headMutation.error)}
              </p>
            )}
            <div className="flex justify-end">
              <button disabled={!changing || reason.length < 5 || headMutation.isPending} className="btn btn-primary">
                {headMutation.isPending ? "Saving…" : "Change head"}
              </button>
            </div>
          </form>
        </ActionPanel>

        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Leadership history</h4>
          {history(
            (department.head_changes ?? []).map((change) => (
              <li key={change.id} className="space-y-1 px-4 py-3">
                <p className="text-sm font-medium text-gray-900">
                  {change.previous_head_name || "Nobody"} → {change.new_head_name || "Nobody"}
                </p>
                <p className="text-xs text-gray-500">
                  {change.created_at.slice(0, 10)}
                  {change.changed_by_name ? ` · by ${change.changed_by_name}` : ""}
                </p>
                <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-700">{change.reason}</p>
              </li>
            )),
            "No changes of head recorded yet.",
          )}
        </section>
      </DetailsModal>
    );
  };

  const positionDetails = (position: PositionRow) => {
    const holders = (profiles.data ?? []).filter((profile) => profile.position === position.id && inPost(profile));
    return (
      <DetailsModal
        title={position.title}
        headline={{
          label: "Staff in post",
          value: String(position.holders),
          note: position.holders ? `${positionDepartment(position)} · ${levelLabel(position.level)}` : `Vacant · ${positionDepartment(position)}`,
        }}
        status={activeStatus(position)}
        facts={[
          ["Department", positionDepartment(position)],
          ["Level", levelLabel(position.level)],
          ["Experience required", position.experience_required ? `${position.experience_required} ${position.experience_required === 1 ? "year" : "years"}` : "None"],
          ["Pay range", salaryRange(position) || "Not set"],
        ]}
        notice={deleteNotice(position, "Deactivate it to stop it being offered on new staff profiles.")}
        sections={[
          { title: "Description", body: position.description },
          { title: "Qualifications", body: position.qualifications },
        ]}
        onClose={() => setViewing(null)}
        footer={detailsFooter(
          "positions",
          position,
          position.title,
          "Position",
          "Nobody has held this position, so it can be removed outright. Deactivate instead if you may reopen it later.",
          () => openForm({ kind: "positions", record: position }),
        )}
      >
        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Who holds it</h4>
          {history(
            holders.map((profile) => (
              <li key={profile.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm">
                <span className="font-medium text-gray-900">{profile.user_name}</span>
                <span className="text-xs text-gray-500">
                  {profile.employee_id} · {profile.department_name || "School-wide"} · <span className="capitalize">{profile.employment_status}</span>
                </span>
              </li>
            )),
            "Nobody is in post. The position is vacant.",
          )}
        </section>
      </DetailsModal>
    );
  };

  const leaveDetails = (policy: LeaveType) => (
    <DetailsModal
      title={policy.name}
      headline={{
        label: "Allowance",
        value: `${policy.max_days_per_year} days a year`,
        note: `${policy.is_paid ? "Paid" : "Unpaid"} · ${policy.requires_approval ? "needs approval" : "no approval needed"}`,
      }}
      status={activeStatus(policy)}
      facts={[
        ["Code", <span className="font-mono">{policy.code}</span>],
        ["Pay", policy.is_paid ? "Paid" : "Unpaid"],
        ["Approval", policy.requires_approval ? "Required" : "Not required"],
        ["Requests taken", String(leaveRequestsTaken(policy))],
      ]}
      notice={deleteNotice(policy, "Deactivate it so staff can no longer choose it; the leave already taken stays on record.")}
      sections={[{ title: "Description", body: policy.description }]}
      onClose={() => setViewing(null)}
      footer={detailsFooter(
        "leave",
        policy,
        policy.name,
        "Leave policy",
        "No leave has been taken under this policy, so it can be removed outright.",
        () => openForm({ kind: "leave", record: policy }),
      )}
    />
  );

  const viewed = (() => {
    if (!viewing || editing || dialog) return null;
    if (viewing.kind === "departments") {
      const row = departmentRows?.find((item) => item.id === viewing.id);
      return row ? departmentDetails(row) : null;
    }
    if (viewing.kind === "positions") {
      const row = positionRows?.find((item) => item.id === viewing.id);
      return row ? positionDetails(row) : null;
    }
    const row = leaveTypes.data?.find((item) => item.id === viewing.id);
    return row ? leaveDetails(row) : null;
  })();

  // --- Page ------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
          <Users className="h-6 w-6" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HR setup</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            The departments, positions and leave policies that staff profiles, leave and reports are built on.
          </p>
        </div>
      </div>

      {isFirstRun && (
        <section className="card overflow-hidden">
          <div className="card-titlebar">
            <div>
              <h2 className="font-semibold text-gray-900">Set up your HR structure</h2>
              <p className="mt-1 text-sm text-gray-500">Nothing has been configured yet. These three steps build what staff profiles, leave and reporting depend on.</p>
            </div>
          </div>
          <ol className="divide-y divide-gray-100">
            {SETUP_STEPS.map((step, index) => {
              const Icon = TAB_META[step.id].icon;
              return (
                <li key={step.id} className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Step {index + 1}</span>
                    <div className="mt-0.5 font-semibold text-gray-900">{step.title}</div>
                    <p className="mt-0.5 text-sm text-gray-500">{step.body}</p>
                  </div>
                  <button type="button" onClick={() => addFor(step.id)} className={`shrink-0 ${index === 0 ? "btn btn-primary" : "btn btn-outline"}`}>
                    <Plus className="mr-2 h-4 w-4" />
                    {step.action}
                  </button>
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {!isFirstRun && (
        <div role="tablist" aria-label="HR setup sections" className="grid gap-3 sm:grid-cols-3">
          {TABS.map((item) => {
            const meta = TAB_META[item];
            const active = tab === item;
            return (
              <button
                key={item}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => selectTab(item)}
                className={`flex items-center gap-4 rounded-xl border bg-white p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  active ? "border-primary-500 ring-1 ring-primary-500" : "border-gray-200 hover:border-primary-300"
                }`}
              >
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${active ? "bg-primary-600 text-white" : "bg-primary-50 text-primary-700"}`}>
                  <meta.icon className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="flex items-baseline gap-2">
                    <span className={`text-2xl font-semibold leading-none ${counts[item] ? "text-gray-900" : "text-gray-300"}`}>
                      {countsLoading ? "–" : counts[item]}
                    </span>
                    <span className="text-sm font-medium text-gray-800">{meta.label}</span>
                  </span>
                  <span className="mt-1 block truncate text-xs text-gray-500">{countsLoading ? meta.subtitle : attention[item]}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {problem && (
        <div role="alert" className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <span className="flex-1">{problem}</span>
          <button type="button" onClick={() => setProblem("")} aria-label="Dismiss" className="rounded p-1 text-red-700/70 hover:bg-red-100 hover:text-red-900">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {message && (
        <div role="status" className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <span className="flex-1">{message}</span>
          <button type="button" onClick={() => setMessage("")} aria-label="Dismiss" className="rounded p-1 text-green-700/70 hover:bg-green-100 hover:text-green-900">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {!isFirstRun && tab === "departments" && (
        <ClientRegister
          title="Departments"
          description="Open a department to edit it, change its head or see its leadership history."
          actions={addButton("departments")}
          rows={departmentRows}
          config={departmentConfig}
          columns={departmentColumns}
          rowKey={(row) => row.id}
          onOpen={(row) => open("departments", row.id)}
          noun={["department", "departments"]}
          exportName={{ file: "departments", title: "Departments" }}
          isLoading={departments.isLoading}
          isError={departments.isError}
          onRetry={() => departments.refetch()}
          emptyText="No departments yet. Add the first one."
        />
      )}
      {!isFirstRun && tab === "positions" && (
        <ClientRegister
          title="Positions"
          description="The job titles offered on staff profiles. In post counts active, probation and suspended staff."
          actions={addButton("positions")}
          rows={positionRows}
          config={positionConfig}
          columns={positionColumns}
          rowKey={(row) => row.id}
          onOpen={(row) => open("positions", row.id)}
          noun={["position", "positions"]}
          exportName={{ file: "positions", title: "Positions" }}
          isLoading={positions.isLoading}
          isError={positions.isError}
          onRetry={() => positions.refetch()}
          emptyText="No positions yet. Add the first one."
        />
      )}
      {!isFirstRun && tab === "leave" && (
        <ClientRegister
          title="Leave policies"
          description="The kinds of leave staff can request, and how much of each."
          actions={addButton("leave")}
          rows={leaveTypes.data}
          config={leaveConfig}
          columns={leaveColumns}
          rowKey={(row) => row.id}
          onOpen={(row) => open("leave", row.id)}
          noun={["leave policy", "leave policies"]}
          exportName={{ file: "leave-policies", title: "Leave Policies" }}
          isLoading={leaveTypes.isLoading}
          isError={leaveTypes.isError}
          onRetry={() => leaveTypes.refetch()}
          emptyText="No leave policies yet. Add the first one."
        />
      )}

      {viewed}

      {editing?.kind === "departments" && (
        <DepartmentForm
          record={editing.record}
          busy={saveDepartment.isPending}
          error={saveDepartment.isError ? errorText(saveDepartment.error) : undefined}
          onSave={(data) => saveDepartment.mutate({ ...data, id: editing.record?.id })}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.kind === "positions" && (
        <PositionForm
          record={editing.record}
          departments={departments.data ?? []}
          busy={savePosition.isPending}
          error={savePosition.isError ? errorText(savePosition.error) : undefined}
          onSave={(data) => savePosition.mutate({ ...data, id: editing.record?.id })}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.kind === "leave" && (
        <LeavePolicyForm
          record={editing.record}
          busy={saveLeave.isPending}
          error={saveLeave.isError ? errorText(saveLeave.error) : undefined}
          onSave={(data) => saveLeave.mutate({ ...data, id: editing.record?.id })}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog request={dialog} onClose={() => setDialog(null)} busy={deleting} />
    </div>
  );
}
